import {
  ODataQuery,
  ValidControlCategory,
  ValidControlCode,
  ValidPartnerEventCode,
} from "@theprelude/sdk";
import { ReactNode } from "react";
import { z } from "zod";
import { filtersSchema } from "@/lib/filter";
import { validControlSchema } from "@/lib/validators";

export type ODataObjectFilter<T> = object &
  NonNullable<ODataQuery<T>["filter"]>;

type ValueOrEvaluatedValue<T> =
  | T
  | ((control: ValidControlCode | undefined) => T);

export interface ViewDefinition<T> {
  title: ValueOrEvaluatedValue<string>;
  description?: ValueOrEvaluatedValue<ReactNode>;
  query: ODataObjectFilter<T>;
  controlCategory?: ValueOrEvaluatedValue<ValidControlCategory | undefined>;
  partnerEvents?: ValidPartnerEventCode[];
}

export const buildViewSchema = <V extends z.EnumLike, O extends string>(opts: {
  overlay: O;
  views: V;
  default: V[keyof V];
}) =>
  z.tuple([z.literal(opts.overlay)]).or(
    z.tuple([
      z.literal(opts.overlay),
      z.object({
        view: z.nativeEnum(opts.views).optional().catch(opts.default),
        filters: filtersSchema.optional().catch([]),
        control: validControlSchema.optional(),
        instanceId: z.string().uuid().optional(),
      }),
    ]),
  );

export const getView = <TView extends string, TDef>(
  views: Record<TView, ViewDefinition<TDef>>,
  view: TView,
  control: ValidControlCode | undefined,
) => {
  const {
    title: getTitle,
    description: getDescription,
    controlCategory: getControlCategory,
    ...rest
  } = views[view];

  const title = typeof getTitle === "function" ? getTitle(control) : getTitle;
  const description =
    typeof getDescription === "function"
      ? getDescription(control)
      : getDescription;
  const controlCategory =
    typeof getControlCategory === "function"
      ? getControlCategory(control)
      : getControlCategory;

  return { ...rest, title, description, controlCategory };
};
